/* eslint-disable array-callback-return */
import React, {
  FunctionComponent,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import styles from "./CheckoutInfo.module.scss";
import { MultiStepProgress } from "components/MultiStepProgress";
import { Collapsible } from "components/basic/Collapsible";
import { SelectComponent as Select } from "components/basic/Select";
import { ButtonRound } from "components/basic/Buttons";
import { CompoundSlider as Slider } from "components/basic/Slider";
import { Spinner } from "components/basic/Spinner";
import imgVehicle from "@/assets/img/car_template.png";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import { V12Calculator } from "@/pages/Finance/V12Calculator";
import {
  selectBasicInfo,
  fetchBasicInfo,
  fetchFinanceOptions,
  selectFinanceOptions,
  selectFinancePrice,
  selectMaximumDeposit,
  submitFinanceOption,
  selectCreditInformation,
  selectBasicInfoStatus,
  selectDekoOptionStatus,
  selectCreditInfoStatus,
  payAction,
  selectFaqs,
  selectSourcecode,
} from "./checkoutInfoSlice";
import { numberWithCommas } from "@/core/helpers/formatHelper";
import { progressInfosFactory } from "@/pages/checkout/constants";
import ReactGA from 'react-ga';

const EnquiryForm = React.lazy(
  () => import("@/components/EnquiryForm/EnquiryForm")
);

type Props = {};

const VAT_PRICE = 1.2;

const POUND = "\u00A3";
const CURRENT_PAGE_NUMBER = 0;

const CheckoutInfo: FunctionComponent<Props> = (props: Props) => {
  const [value, setValue] = useState(30);
  const [availablePurchase, setAvailablePurchase] = useState(true);
  const [sliderDomain, setSliderDomain] = useState<[number, number]>([0, 50]);

  const [registrationMarkPrice, setRegistrationMarkPrice] = useState("0");
  const [dvlaFee, setDvlaFee] = useState("0");
  const [totalPrice, setTotalPrice] = useState(0);
  const [open, setOpen] = useState(true);
  const [showFinance, setShowFinance] = useState(false);
  const [options, setOption] = useState<Array<string>>([]);
  const [optionKeys, setOptionKeys] = useState<Array<string>>([]);
  const [optionValue, setOptionValue] = useState("");
  const [forceDepositMax, setForceDepositMax] = useState(false);
  const [isDvlaPlate, setIsDvlaPlate] = useState(false);
  const [needVTARegisteredShow, setNeedVTARegisteredShow] = useState(false);
  const [selectedFinanceOption, setSelectedFinanceOption] = useState('');

  const [currentOption, setCurrentOption] = useState<{
    title: string;
    group: string;
    name: string;
    term: string;
    interest: number;
  }>();
  const [_depositAmount, setDepositAmount] = useState(0);

  const enquiryFormRef = useRef(null);

  const { sel, registration }: { sel: string; registration: string } =
    useParams() as { sel: string; registration: string };
  const basicInfo = useAppSelector(selectBasicInfo);
  const financeOptions = useAppSelector(selectFinanceOptions);
  const financePrice = useAppSelector(selectFinancePrice);
  const maximumDeposit = useAppSelector(selectMaximumDeposit);
  const creditInformation = useAppSelector(selectCreditInformation);
  const basicStatus = useAppSelector(selectBasicInfoStatus);
  const dekoStatus = useAppSelector(selectDekoOptionStatus);
  const financeStatus = useAppSelector(selectCreditInfoStatus);
  const sourcecode = useAppSelector(selectSourcecode);
  const faqs = useAppSelector(selectFaqs);
  const dispatch = useAppDispatch();

  const progressInfos = useMemo(
    () => progressInfosFactory({ registration }),
    [registration]
  );
  const progress = useMemo(
    () => progressInfos.map((info) => info.title),
    [progressInfos]
  );

  const navigate = useNavigate();

  const [calculator] = useState(() => new V12Calculator());

  const nextPage = (isFinance: boolean) => {
    let url = progressInfos[CURRENT_PAGE_NUMBER + 1].path;
    if (isFinance) {
      url = url + "?finance=true";
    }
    navigate(url);
  };

  const regPreview =
    "https://www.newreg.co.uk/reg.php?reg=" +
    registration +
    "&logo=no_logo&amp;border=border_yes&amp;size=regular-front";

  const nf = new Intl.NumberFormat("en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  useEffect(() => {
    dispatch(fetchBasicInfo({ sel, registration }));
    dispatch(fetchFinanceOptions({ registration }));
    document.title = "Information about " + registration.replace("+", " ");
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load saved finance details from sessionStorage
  useEffect(() => {
    try {
      const savedFinanceDetails = sessionStorage.getItem('selectedFinanceDetails');
      if (savedFinanceDetails) {
        const { depositPercent, optionKey } = JSON.parse(savedFinanceDetails);
        if (depositPercent !== undefined) {
          setValue(depositPercent);
        }
        if (optionKey) {
          setOptionValue(optionKey);
        }
      }
    } catch (error) {
      console.error('Error loading saved finance details:', error);
    }
  }, []);

  useEffect(() => {
    setForceDepositMax(maximumDeposit < 50);
  }, [maximumDeposit]);

  // calculate slider domain
  const calculateSliderDomain = (key: string): [number, number] => {
    if (!financeOptions[key] || !financePrice) return [0, 50];
    
    const option = financeOptions[key];
    const minLoan = typeof option.minLoan === 'string' ? parseFloat(option.minLoan) : option.minLoan;
    const maxLoan = typeof option.maxLoan === 'string' ? parseFloat(option.maxLoan) : option.maxLoan;
    
    // calculate min deposit percent
    let minDepositPercent = (financePrice - maxLoan) / financePrice * 100;
    minDepositPercent = Math.max(0, minDepositPercent);
    
    // calculate max deposit percent
    let maxDepositPercent = (financePrice - minLoan) / financePrice * 100;
    maxDepositPercent = Math.min(100, maxDepositPercent);
    
    // consider maximumDeposit limit
    maxDepositPercent = Math.min(maxDepositPercent, maximumDeposit);
    
    // ensure minDepositPercent <= maxDepositPercent
    if (minDepositPercent > maxDepositPercent) {
      return [0, maximumDeposit > 0 ? Math.floor(maximumDeposit) : 0];
    }
    
    // since the slider is integer step, we need to convert the percentage value to integer
    // for min value, ceil, ensure Credit Amount not exceed maxLoan
    // for max value, floor, ensure Credit Amount not less than minLoan
    
    // calculate min deposit percent
    const ceiledMinDepositPercent = Math.ceil(minDepositPercent);
    
    // check if the min deposit percent is less than maxLoan
    const depositAmountForCeiledMin = (financePrice / 100) * ceiledMinDepositPercent;
    const creditAmountForCeiledMin = financePrice - depositAmountForCeiledMin;
    
    // if the min deposit percent is less than maxLoan, use the original min deposit percent
    let finalMinDepositPercent = creditAmountForCeiledMin <= maxLoan ? ceiledMinDepositPercent : Math.floor(minDepositPercent);
    
    // calculate max deposit percent
    const flooredMaxDepositPercent = Math.floor(maxDepositPercent);
    
    // check if the max deposit percent is less than minLoan
    const depositAmountForFlooredMax = (financePrice / 100) * flooredMaxDepositPercent;
    const creditAmountForFlooredMax = financePrice - depositAmountForFlooredMax;
    
    // if the max deposit percent is less than minLoan, adjust the max deposit percent
    let finalMaxDepositPercent = flooredMaxDepositPercent;
    if (creditAmountForFlooredMax < minLoan) {
      // recalculate to ensure Credit Amount equals minLoan
      const adjustedMaxDepositPercent = ((financePrice - minLoan) / financePrice) * 100;
      // floor, ensure Credit Amount not less than minLoan
      finalMaxDepositPercent = Math.floor(adjustedMaxDepositPercent);
    }
    
    // ensure max deposit percent not exceed 50% （v12 default max deposit percent）
    finalMaxDepositPercent = Math.min(finalMaxDepositPercent, 50);
    
    // ensure min value not greater than max value
    if (finalMinDepositPercent > finalMaxDepositPercent) {
      finalMinDepositPercent = finalMaxDepositPercent;
    }
    
    return [finalMinDepositPercent, finalMaxDepositPercent];
  };

  useEffect(() => {
    const arr: Array<string> = [];
    const arr2: Array<string> = [];
    
    // Filter finance products that meet loan amount requirements
    const validFinanceOptions: { [key: string]: any } = {};
    let hasValidOption = false;
    let savedDepositPercent = value; // default deposit percent
    
    for (let key in financeOptions) {
      const option = financeOptions[key];
      const actualLoanAmount = financePrice;
      
      if (actualLoanAmount >= option.minLoan && actualLoanAmount <= option.maxLoan) {
        validFinanceOptions[key] = option;
        arr.unshift(option.name);
        arr2.unshift(key);
        hasValidOption = true;
      }
    }

    if (!hasValidOption && Object.keys(financeOptions).length > 0) {
      // if no valid finance options, hide finance
      setShowFinance(false);
      return;
    }
    
    setOption(arr);
    setOptionKeys(arr2);

    // Try to get saved finance option from sessionStorage
    try {
      const savedFinanceDetails = sessionStorage.getItem('selectedFinanceDetails');
      if (savedFinanceDetails) {
        const { optionKey, depositPercent } = JSON.parse(savedFinanceDetails);
        
        // save cached depositPercent
        if (depositPercent !== undefined) {
          savedDepositPercent = depositPercent;
          setValue(depositPercent);
        }
        
        // Check if saved option is still valid
        if (optionKey && arr2.includes(optionKey)) {
          setOptionValue(optionKey);
          setSelectedFinanceOption(validFinanceOptions[optionKey].name);
          setCurrentOption(validFinanceOptions[optionKey]);
          
          // calculate and set slider domain
          const domain = calculateSliderDomain(optionKey);
          setSliderDomain(domain as [number, number]);
          
          calculateFinanceFigures(optionKey, savedDepositPercent); // use cached depositPercent
          return;
        }
      }
    } catch (error) {
      console.error('Error loading saved finance option:', error);
    }

    // If no saved option or saved option is invalid, use default
    setOptionValue(arr2[0]);
    setSelectedFinanceOption(arr[0]);
    if (arr2.length > 0) {
      const firstKey = arr2[0];
      setCurrentOption(validFinanceOptions[firstKey]);
      
      // calculate and set slider domain
      const domain = calculateSliderDomain(firstKey);
      setSliderDomain(domain as [number, number]);
      
      calculateFinanceFigures(firstKey, savedDepositPercent); // use cached depositPercent
    }
  }, [financeOptions, financePrice]);

  // add a useEffect to listen to the change of optionValue, update slider domain
  useEffect(() => {
    if (optionValue) {
      const domain = calculateSliderDomain(optionValue);
      setSliderDomain(domain as [number, number]);
    }
  }, [optionValue, financePrice, maximumDeposit]);

  useEffect(() => {
    const registrationDetails = basicInfo?.registrationDetails;
    if (registrationDetails) {
      if (parseFloat(registrationDetails.display_price || "0") > 0) {
        if (!availablePurchase) {
          setAvailablePurchase(true);
        }
      } else {
        setAvailablePurchase(false);
        return;
      }
    }

    if (
      registrationDetails &&
      ["134", "1", "57", "38"].indexOf(registrationDetails.sourcecode) !== -1 &&
      parseInt(registrationDetails.price) < 25000
    ) {
      setShowFinance(true);
    }

    if (basicInfo?.pricingType === "site_pricing_standard") {
      setRegistrationMarkPrice(
        basicInfo?.registrationDetails.originalPrice ||
          basicInfo?.registrationDetails.price
      );
      setDvlaFee(basicInfo?.registrationDetails.transfer_fee);

      let dotVatPrice = 0;
      if (basicInfo?.registrationDetails.web_vat) {
        dotVatPrice = returnVatOfPriceOnly(
          basicInfo?.registrationDetails.transfer_fee
        );
      }

      const totalNoVatPrice =
        parseFloat(
          basicInfo?.registrationDetails.originalPrice ||
            basicInfo?.registrationDetails.price
        ) + parseFloat(basicInfo?.registrationDetails.transfer_fee);

      const totalVatPrice =
        parseFloat(basicInfo?.registrationDetails.vat) + dotVatPrice;
      setTotalPrice(totalNoVatPrice + totalVatPrice);
    }

    if (basicInfo?.pricingType === "site_pricing_dvla") {
      setIsDvlaPlate(true);
      setRegistrationMarkPrice(basicInfo?.registrationDetails.price);
      setDvlaFee(basicInfo?.registrationDetails.transfer_fee);
      setTotalPrice(
        basicInfo?.registrationDetails.price_plus_vat +
          10.74 +
          parseFloat(basicInfo?.registrationDetails.transfer_fee)
      );
    }

    if (basicInfo?.pricingType === "site_pricing_stock") {
      const registrationNoVatPrice = basicInfo?.registrationDetails
        .originalPrice
        ? basicInfo?.registrationDetails.originalPrice
        : basicInfo?.registrationDetails.price;
      setRegistrationMarkPrice(registrationNoVatPrice);
      setDvlaFee(basicInfo?.registrationDetails.transfer_fee);
      const totalNoVatPrice =
        parseFloat(
          basicInfo?.registrationDetails.originalPrice ||
            basicInfo?.registrationDetails.price
        ) + returnPriceMinusVat(basicInfo?.registrationDetails.transfer_fee);
      const totalVatPrice =
        parseFloat(basicInfo?.registrationDetails.vat) +
        returnVatOfPrice(basicInfo?.registrationDetails.transfer_fee);
      setTotalPrice(totalNoVatPrice + totalVatPrice);
    }

    if (
      basicInfo?.registrationDetails.sourcecode !== "134" &&
      basicInfo?.registrationDetails.display_vat &&
      parseFloat(basicInfo?.registrationDetails.display_vat) > 0 &&
      basicInfo?.pricingType !== "site_pricing_dvla"
    ) {
      setNeedVTARegisteredShow(true);
    }
  }, [basicInfo]);
    
  useEffect(()=>{
    ReactGA.pageview(window.location.pathname);
  }, [])

  const calculateFinanceFigures = (
    key: string,
    depositPercent: number
  ) => {
    // ensure depositPercent is in the valid range
    const domain = calculateSliderDomain(key);
    const minDepositPercent = domain[0];
    const maxDepositPercent = domain[1];
    
    // since the slider is integer step, ensure depositPercent is integer
    let adjustedDepositPercent = Math.round(depositPercent);
    
    // adjust depositPercent, ensure it is in the valid range
    if (adjustedDepositPercent < minDepositPercent) {
      adjustedDepositPercent = minDepositPercent;
    } else if (adjustedDepositPercent > maxDepositPercent) {
      adjustedDepositPercent = maxDepositPercent;
    }
    
    // if depositPercent exceeds maximumDeposit, use maximumDeposit
    const _despositPercent =
      adjustedDepositPercent > maximumDeposit ? Math.floor(maximumDeposit) : adjustedDepositPercent;
    setValue(_despositPercent);
    let _depositAmount = (financePrice / 100) * _despositPercent;
    
    if (financePrice < 250 && maximumDeposit === 0) {
      _depositAmount = 0;
      setValue(0);
    }
    
    // ensure Credit Amount not less than minLoan
    const option = financeOptions[key];
    const minLoan = typeof option.minLoan === 'string' ? parseFloat(option.minLoan) : option.minLoan;
    let creditAmount = financePrice - _depositAmount;
    
    if (creditAmount < minLoan) {
      // if Credit Amount is less than minLoan, adjust deposit amount
      _depositAmount = Math.max(0, financePrice - minLoan);
      // recalculate depositPercent, ensure it is integer
      const newDepositPercent = Math.floor((_depositAmount / financePrice) * 100);
      setValue(newDepositPercent);
      // use the new depositPercent to recalculate deposit amount, ensure precise
      _depositAmount = (financePrice / 100) * newDepositPercent;
    }
    
    _depositAmount = Math.ceil(parseFloat(_depositAmount.toString()) * 100) / 100;
    setDepositAmount(_depositAmount);
    console.log('financePrice', financePrice);
    console.log('maximumDeposit', maximumDeposit);
    console.log('_depositAmount', _depositAmount);
    console.log('Credit Amount', financePrice - _depositAmount);
    if (!financeOptions[key]) return;

    const selectedProduct = {
      productId: parseInt(financeOptions[key].productId),
      productGuid: financeOptions[key].productGuid,
      name: financeOptions[key].name,
      minLoan: financeOptions[key].minLoan,
      maxLoan: financeOptions[key].maxLoan,
      months: parseInt(financeOptions[key].term),
      monthlyRate: financeOptions[key].monthlyRate,
      apr: financeOptions[key].interest,
      serviceFee: financeOptions[key].serviceFee,
      documentFee: financeOptions[key].documentFee,
      documentFeeCollectionMonth: financeOptions[key].documentFeeCollectionMonth,
      documentFeeMaximum: financeOptions[key].documentFeeMaximum,
      documentFeeMinimum: financeOptions[key].documentFeeMinimum,
      documentFeePercentage: financeOptions[key].documentFeePercentage,
      optionPeriod: financeOptions[key].optionPeriod,
      deferredPeriod: financeOptions[key].deferredPeriod,
      tag: key,
      altTag: financeOptions[key].altTag,
      settlementFee: financeOptions[key].settlementFee,
      calculationFactor: financeOptions[key].calculationFactor
    };

    const result = calculator.calculate(selectedProduct, financePrice, _depositAmount);
    // Format payment_regular_detail based on payment information
    const months = result.months;
    const initialPayments = result.initialPayments ? result.initialPayments : '0.00';
    const finalPayment = result.finalPayment ? result.finalPayment : '0.00';
    let payment_regular_detail = '';
    if (initialPayments === finalPayment) {
      payment_regular_detail = `${months} x ${POUND}${initialPayments}`;
    } else {
      payment_regular_detail = `${months - 1} x ${POUND}${initialPayments} + ${POUND}${finalPayment}`;
    }
    console.log('result', result);
    dispatch(
      submitFinanceOption({
        amount: financePrice,
        product: financeOptions[key].productId,
        deposit: _depositAmount,
        calculationResult: {
          apr: result.apr,
          payment_regular: result.initialPayments,
          payment_regular_detail: payment_regular_detail,
          loan_repayment: (parseFloat(result.balancePayable) + _depositAmount).toFixed(2),
          loan_cost: result.chargeForCredit,
          annual_rate: result.annualRate
        }
      })
    );

    setCurrentOption(financeOptions[key]);
  };

  const sliderExample = () => {
    return (
      <Slider
        domain={sliderDomain}
        value={value}
        onValueChange={(value: Array<number>) => {
          calculateFinanceFigures(optionValue, value[0]);
        }}
      />
    );
  };

  const returnVatOfPriceOnly = (price: string) => {
    const _price = parseFloat(price);
    return _price * VAT_PRICE - _price;
  };

  const returnPriceMinusVat = (price: string) => {
    const _price = parseFloat(price);
    return _price * (1 / VAT_PRICE);
  };

  const returnVatOfPrice = (price: string) => {
    const vat = returnPriceMinusVat(price);
    return parseFloat(price) - vat;
  };

  const scrollToFinance = () => {
    setOpen(true);
    window.location.href = "#finance";

    // const dom = document.getElementById("finance");
    // const distance = (dom as HTMLElement).getBoundingClientRect().top;
    // window.scrollTo({
    //   top: distance - 5,
    //   behavior: "smooth",
    // });
  };

  const renderPurchaseJsx = () => {
    return (
      <>
        <div className={styles.container}>
          <div className="pt-2">
            <h1 className={`${styles.title} pl-4 mt-6`}>
                Number plates information for {registration.replace("+", " ")}
            </h1>
            <div
              className="grid md:grid-cols-3 sm:grid-cols-1 gap-4 relative"
              style={{ minHeight: 300 }}
            >
              {basicStatus && <Spinner />}
              <div
                className={`md:col-span-2 p-4 ${
                  basicStatus ? "opacity-30" : ""
                }`}
              >
                <div
                  className={`bg-card-bg-grey text-left p-6 h-full grid grid-cols-1 divide-y`}
                >
                  {basicInfo?.pricingType === "site_pricing_standard" && (
                    <>
                      <p className="d-flex justify-between py-4 font-bold">
                        <span>Registration Mark</span>
                        <span>
                          {POUND}
                          {numberWithCommas(parseFloat(registrationMarkPrice))}
                        </span>
                      </p>
                      <p className="d-flex justify-between py-4 font-normal">
                        <span className="">Compulsory DVLA transfer fee</span>
                        <span>
                          {POUND}
                          {numberWithCommas(parseFloat(dvlaFee))}
                        </span>
                      </p>
                      {parseFloat(basicInfo?.registrationDetails.vat) > 0 ? (
                        <p className="d-flex justify-between py-4 font-normal">
                          <span className="">VAT @ 20%</span>
                          <span>
                            {POUND}
                            {numberWithCommas(
                              parseFloat(basicInfo?.registrationDetails.vat)
                            )}
                          </span>
                        </p>
                      ) : (
                        <></>
                      )}
                      <p className="d-flex justify-between py-4 text-lime-600 font-bold">
                        <span className="">Total for Registration Mark</span>
                        <span>
                          {POUND}
                          {numberWithCommas(totalPrice)}
                        </span>
                      </p>
                    </>
                  )}

                  {basicInfo?.pricingType === "site_pricing_dvla" && (
                    <>
                      <p className="d-flex justify-between py-4 font-bold">
                        <span>Registration Mark</span>
                        <span>
                          {POUND}
                          {numberWithCommas(parseFloat(registrationMarkPrice))}
                        </span>
                      </p>
                      <p className="d-flex justify-between py-4 font-normal">
                        <span className="">Standard Postage</span>
                        <span>{POUND}8.95</span>
                      </p>
                      <p className="d-flex justify-between py-4 font-normal">
                        <span className="">VAT @ 20%</span>
                        <span>
                          {POUND}
                          {numberWithCommas(
                            parseFloat(basicInfo?.registrationDetails.vat) +
                              1.79
                          )}
                        </span>
                      </p>
                      <p className="d-flex justify-between py-4 font-normal">
                        <span className="">DVLA Transfer Fee</span>
                        <span>
                          {POUND}
                          {numberWithCommas(
                            parseFloat(
                              basicInfo?.registrationDetails.transfer_fee
                            )
                          )}
                        </span>
                      </p>
                      <p className="d-flex justify-between py-4 text-lime-600 font-bold">
                        <span className="">Total for Registration Mark</span>
                        <span>
                          {POUND}
                          {numberWithCommas(totalPrice)}
                        </span>
                      </p>
                      {isDvlaPlate && (
                        <p
                          style={{
                            color: "#333",
                            fontSize: "12px",
                            lineHeight: "125%",
                          }}
                        >
                          New Reg will supply the{" "}
                          <b>Certificate of Entitlement</b> for this
                          registration mark, which is valid for 10 years from
                          the date of purchase. If you do not assign this
                          registration mark to a vehicle within 10 years, you
                          must renew it. This is ideal if you have not yet taken
                          delivery of your new vehicle but wish to reserve{" "}
                          {basicInfo?.registrationDetails.registration} in
                          advance. If you are buying{" "}
                          {basicInfo?.registrationDetails.registration} as a
                          gift for someone else, the Certificate of Entitlement
                          acts as a gift certificate and we can even send your
                          communication in plain envelopes so you can keep it a
                          surprise.
                        </p>
                      )}
                    </>
                  )}
                  {needVTARegisteredShow && (
                    <p>
                      If our donor is not VAT registered, then the price will be
                      amended accordingly.
                    </p>
                  )}
                  {basicInfo?.pricingType === "site_pricing_stock" && (
                    <>
                      <p className="d-flex justify-between py-4 font-bold">
                        <span>Registration Mark</span>
                        <span>
                          {POUND}
                          {numberWithCommas(parseFloat(registrationMarkPrice))}
                        </span>
                      </p>
                      <p className="d-flex justify-between py-4 font-normal">
                        <span className="">Compulsory DVLA transfer fee</span>
                        <span>
                          {POUND}
                          {numberWithCommas(parseFloat(dvlaFee))}
                        </span>
                      </p>
                      {parseInt(basicInfo?.registrationDetails.vat) > 0 ? (
                        <p className="d-flex justify-between py-4 font-normal">
                          <span className="">VAT @ 20%</span>
                          <span>
                            {POUND}
                            {numberWithCommas(
                              parseFloat(basicInfo?.registrationDetails.vat)
                            )}
                          </span>
                        </p>
                      ) : (
                        <></>
                      )}
                      <p className="d-flex justify-between py-4 text-lime-600 font-bold">
                        <span className="">Total for Registration Mark</span>
                        <span>
                          {POUND}
                          {numberWithCommas(totalPrice)}
                        </span>
                      </p>
                    </>
                  )}
                  {!basicStatus && (
                    <>
                      <div
                        className="mt-6 text-center"
                        style={{ borderStyle: "none" }}
                      >
                        <ButtonRound
                          text="Pay by card"
                          style={{ height: "40px", width: 200 }}
                          type="success"
                          className="submit-info-card-btn"
                          onClick={() => {
                            dispatch(
                              payAction({
                                registration,
                                continueCallback() {
                                  nextPage(false);
                                },
                              })
                            );
                          }}
                        />
                      </div>
                      {showFinance && (
                        <div
                          className={`mt-6 text-center ${styles.payByFinance}`}
                          style={{ borderStyle: "none" }}
                        >
                          <ButtonRound
                            text="Pay by finance"
                            style={{ height: "40px", width: 200 }}
                            type="primary"
                            className="open-finance-bth"
                            onClick={scrollToFinance}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className={`p-4 ${basicStatus ? "opacity-30" : ""}`}>
                <div
                  className={`p-4 bg-card-bg-grey h-full relative items-center flex`}
                >
                  <img
                    src={imgVehicle}
                    className="w-full object-cover"
                    alt=""
                  />
                  <img
                    src={regPreview}
                    alt={registration}
                    className={`absolute inset-x-0 m-auto ${styles.previewReg}`}
                  />
                </div>
              </div>
            </div>
          </div>
          {showFinance && (
            <div className="mt-4 mb-8 px-4" id="finance">
              <Collapsible
                text="Finance avaliable - no deposit to pay!"
                graphic="thumb_up"
                open={open}
                onClose={() => setOpen(false)}
              >
                <div className={`mt-4 ${styles.financeAvaliable} relative`}>
                  {(dekoStatus || financeStatus) && <Spinner />}
                  <div
                    className={`p-4 md:p-8 bg-card-bg-grey ${
                      dekoStatus || financeStatus ? "opacity-30" : ""
                    }`}
                  >
                    <div className="">
                      <p className="text-left text-base font-semibold">
                        Choose deposit amount:
                      </p>
                      {forceDepositMax && maximumDeposit > 0 && (
                        <p className="text-left p-4 bg-amber-100 text-sm bg-amber-200 mt-4">
                          Our minimum financeable figure is £250, with this
                          transaction the{" "}
                          <b>
                            maximum allowable deposit is {maximumDeposit}{" "}
                            percent
                          </b>
                          .
                        </p>
                      )}
                    </div>
                    <div className="mt-8">{sliderExample()}</div>
                    <div className="mt-8">
                      <Select
                        label=""
                        onChange={(e: React.FormEvent<HTMLSelectElement>) => {
                          const index = (
                            e.currentTarget as unknown as { index: number }
                          ).index;
                          setOptionValue(optionKeys[index]);
                          setSelectedFinanceOption(options[index]);
                          setCurrentOption(financeOptions[optionKeys[index]]);
                          calculateFinanceFigures(optionKeys[index], value);
                        }}
                        defaultValue={selectedFinanceOption}
                        options={options}
                      />
                    </div>
                    <div className={`text-left font-medium mt-4`}>
                      <p className="d-flex justify-between py-4">
                        <span>Cash Price</span>
                        <span>
                          {POUND}
                          {nf.format(financePrice)}
                        </span>
                      </p>
                      <p className="d-flex justify-between py-4">
                        <span>Desposit</span>
                        <span>
                          {POUND}
                          {_depositAmount.toFixed(2)}
                        </span>
                      </p>
                      <p className="d-flex justify-between py-4">
                        <span className="">Representative APR</span>
                        <span>{creditInformation?.apr}%</span>
                      </p>
                      {creditInformation?.annual_rate && (
                        <p className="d-flex justify-between py-4">
                          <span className="">Annual Rate of Interest Fixed</span>
                          <span>{creditInformation?.annual_rate}%</span>
                        </p>
                      )}
                      <p className="d-flex justify-between py-4">
                        <span className="">Monthly Payments</span>
                        <span>
                          {creditInformation?.payment_regular_detail}
                        </span>
                      </p>
                      <p className="d-flex justify-between py-4">
                        <span className="">Total Payable</span>
                        <span>
                          {POUND}
                          {creditInformation?.loan_repayment}
                        </span>
                      </p>

                      <div className="d-flex flex-wrap justify-around py-4 text-sm">
                        <p className="d-flex align-center justify-around w-60 h-10 text-white bg-blue-500 rounded-lg shadow-lg">
                          <span>
                            {POUND}
                            {nf.format(financePrice - _depositAmount)}
                          </span>
                          <span>Credit Amount</span>
                        </p>
                        {/* <p className="d-flex align-center justify-around mt-4 sm:mt-0 w-60 h-10 text-white bg-green-600 rounded-lg shadow-lg">
                          <span>
                            {POUND}
                            {parseFloat(
                              creditInformation?.payment_regular
                            ).toFixed(2)}
                          </span>
                          <span>Monthly Amount</span>
                        </p> */}
                      </div>

                      <div
                        className="mt-6 text-center"
                        style={{ borderStyle: "none" }}
                      >
                        <ButtonRound
                          text="Pay Monthly"
                          style={{
                            padding: "0 3rem",
                            width: "15rem",
                            height: "40px",
                          }}
                          className="submit-info-finance-btn"
                          type="primary"
                          onClick={() => {
                            const financeDetails = {
                              optionKey: optionValue,
                              depositAmount: _depositAmount,
                              depositPercent: value
                            };
                            
                            // Save to sessionStorage
                            sessionStorage.setItem('selectedFinanceDetails', JSON.stringify(financeDetails));
                            
                            dispatch(
                              payAction({
                                registration,
                                finance: true,
                                continueCallback() {
                                  nextPage(true);
                                },
                              })
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Collapsible>
            </div>
          )}
        </div>
        <div className={`py-6 bg-card-bg-grey`}>
          <div className={styles.container}>
            <p className={`${styles.title} px-4`}>Frequently Asked Questions</p>
            {faqs?.map((value, index) => {
              if (index % 2 === 0) {
                return (
                  <div key={index} className="flex flex-row flex-wrap">
                    <div className="md:basis-1/2 basis-full px-2 mt-6">
                      <Collapsible
                        text={value.question}
                        graphic="help_outline"
                        type="gray"
                      >
                        <div style={{ padding: "1rem" }}>
                          <span
                            className={styles.faqContent}
                            dangerouslySetInnerHTML={{ __html: value.answer }}
                          ></span>
                        </div>
                      </Collapsible>
                    </div>
                    {faqs[index + 1] && (
                      <div className="md:basis-1/2 basis-full px-2 mt-6">
                        <Collapsible
                          text={faqs[index + 1].question}
                          graphic="help_outline"
                          type="gray"
                        >
                          <div style={{ padding: "1rem" }}>
                            <span
                              className={styles.faqContent}
                              dangerouslySetInnerHTML={{
                                __html: faqs[index + 1].answer,
                              }}
                            ></span>
                          </div>
                        </Collapsible>
                      </div>
                    )}
                  </div>
                );
              }
            })}
          </div>
        </div>
      </>
    );
  };

  const renderRegisterInterestJsx = () => {
    return (
      <>
        <div className={styles.container}>
          <div className="pt-2">
            <h1 className={`${styles.title} pl-4 mt-6`}>
                Number plates information for {registration}
            </h1>
            <div
              className="grid md:grid-cols-3 sm:grid-cols-1 gap-4 relative"
              style={{ minHeight: 300 }}
            >
              {basicStatus && <Spinner />}
              <div
                className={`md:col-span-2 p-4 ${
                  basicStatus ? "opacity-30" : ""
                }`}
              >
                <div
                  className={`bg-card-bg-grey text-left p-6 h-full flex flex-col justify-center align-center`}
                >
                  <p>
                    This registration not available for purchase, however you
                    can still register interest for <b>{registration}</b> here:
                  </p>
                  <ButtonRound
                    buttonType="button"
                    text="Register Interest"
                    className="h-10 mt-6 w-52"
                    onClick={() => {
                      (enquiryFormRef.current as any)?.openDialog();
                    }}
                  ></ButtonRound>
                  <EnquiryForm
                    ref={enquiryFormRef}
                    registration={registration}
                  ></EnquiryForm>
                </div>
              </div>
              <div className={`p-4 ${basicStatus ? "opacity-30" : ""}`}>
                <div
                  className={`p-4 bg-card-bg-grey h-full relative items-center flex`}
                >
                  <img
                    src={imgVehicle}
                    className="w-full object-cover"
                    alt=""
                  />
                  <img
                    src={regPreview}
                    alt={registration}
                    className={`absolute inset-x-0 m-auto ${styles.previewReg}`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={styles.checkoutInfoPage}>
      <MultiStepProgress stepNames={progress} page={CURRENT_PAGE_NUMBER} />
      {availablePurchase ? renderPurchaseJsx() : renderRegisterInterestJsx()}
    </div>
  );
};

export { CheckoutInfo, CheckoutInfo as default };
